/* eslint-disable react/prop-types */
/**
 * This is a redux-form/formik/etc - agnostic wrapper for adorning a field with label, tooltip,
 * error, etc.
 */
import commonPropTypes from '../../../constants/commonPropTypes'
import React, { Component, useState, useEffect } from 'react'
import { Form, Placeholder } from 'semantic-ui-react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import FieldError from './fieldError'
import FieldTooltip from './fieldTooltip'
import StandardFieldLabel from './standardFieldLabel'
import { getThemedCompact } from '../util'
import { useApplicationTheme } from '../../applicationTheme'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const WAIT_FOR_BLUR_MS = 2000

const FIELD_PLACEHOLDER_LENGTHS = {
  SHORT: 'short',
  MEDIUM: 'medium',
  LONG: 'long',
  FULL: 'full',
} as const

type FieldPlaceholderLength =
  (typeof FIELD_PLACEHOLDER_LENGTHS)[keyof typeof FIELD_PLACEHOLDER_LENGTHS]

interface FieldInput {
  name: string
}

export interface FieldProps {
  elmId?: string
  label?: React.ReactNode
  helpContent?: React.ReactNode
  breakAll?: boolean
  hideWrapper?: boolean
  hideLabel?: boolean
  showTooltip?: boolean
  compact?: boolean
  relaxed?: boolean
  className?: string
  required?: boolean
  disabled?: boolean
  pendingApproval?: boolean
  touched?: boolean
  error?: string
  warning?: string
  checkboxLabel?: React.ReactNode // TO BE DEPRECATED
  // onFocus and onBlur are intentionally typed via the index signature
  // to allow component-specific handler signatures (e.g. FocusEvent<HTMLInputElement>)
  // input is intentionally typed via the index signature to accommodate
  // both FieldInput ({ name: string }) and Semantic UI's SemanticShorthandItem
  footer?: React.ComponentType
  autoFocus?: boolean
  labelOnTop?: boolean
  borderBottom?: 'always' | 'none' | 'smallScreensOnly'
  normalFont?: boolean
  isLoading?: boolean
  loadingPlaceholderLength?: FieldPlaceholderLength
  componentName?: string
  tooltipIconStyle?: string
  value?: unknown
  inputLabel?: string
  type?: string
  checked?: boolean
  isChecked?: boolean // deprecated
  options?: Record<string, unknown>[]
  subLabel?: React.ReactNode
  [key: string]: unknown
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RenderFn = (props: any, showError: boolean) => React.ReactNode

interface RenderEditModeProps {
  render: RenderFn
  fieldProps: FieldProps
}

interface RenderEditModeState {
  fieldFocused: boolean
}

class RenderEditMode extends Component<
  RenderEditModeProps,
  RenderEditModeState
> {
  preventBlurTimeout: ReturnType<typeof setTimeout> | null = null
  mountTime: number | null = null

  constructor(props: RenderEditModeProps) {
    super(props)
    this.state = {
      fieldFocused: false,
    }
  }

  componentDidMount() {
    if (this.props.fieldProps?.autoFocus) {
      this.mountTime = Date.now()
    }
  }

  componentWillUnmount() {
    if (this.preventBlurTimeout) {
      clearTimeout(this.preventBlurTimeout)
    }
  }

  shouldPreventBlur = (): boolean => {
    if (!this.mountTime) return false
    const elapsedTime = Date.now() - this.mountTime
    return elapsedTime < WAIT_FOR_BLUR_MS
  }

  render() {
    const {
      render,
      fieldProps: {
        touched,
        error,
        warning,
        label,
        checkboxLabel, // TO BE DEPRECATED
        helpContent,
        breakAll: _breakAll, // eslint-disable-line @typescript-eslint/no-unused-vars
        hideLabel,
        hideWrapper,
        showTooltip,
        compact,
        relaxed,
        onFocus,
        onBlur,
        className,
        elmId,
        required,
        input,
        pendingApproval,
        disabled,
        footer: Footer,
        // not implemented for edit mode... but to omit from restProps
        isLoading: _isLoading, // eslint-disable-line @typescript-eslint/no-unused-vars
        loadingPlaceholderLength: _loadingPlaceholderLength, // eslint-disable-line @typescript-eslint/no-unused-vars
        labelOnTop: _labelOnTop, // eslint-disable-line @typescript-eslint/no-unused-vars
        borderBottom: _borderBottom, // eslint-disable-line @typescript-eslint/no-unused-vars
        normalFont: _normalFont, // eslint-disable-line @typescript-eslint/no-unused-vars
        tooltipIconStyle,
        ...restProps
      },
    } = this.props

    const { fieldFocused } = this.state
    const showError = !!(touched && error)
    const showErrorAbove = showError && compact
    const showErrorBelow = showError && !compact
    const showAnchor = showTooltip || Boolean(helpContent)

    // determining if it's a checkbox.  "isChecked" is a deprecated prop that is considered here for backward compatibility.
    const isCheckbox =
      _.has(restProps, 'checked') || _.has(restProps, 'isChecked')

    const onFocusFn = onFocus as ((e: React.FocusEvent) => void) | undefined
    const onBlurFn = onBlur as ((e: React.FocusEvent) => void) | undefined

    const onFocusNew = (e: React.FocusEvent) => {
      this.setState({ fieldFocused: true })
      if (onFocusFn) {
        onFocusFn(e)
      }
    }

    const onBlurNew = (e: React.FocusEvent): false | void => {
      // This chunk here is to ensure focus isn't stolen from this field (by the likes of the equalweb script)
      const shouldPrevent = this.shouldPreventBlur()

      if (shouldPrevent) {
        const target = e.target as HTMLElement
        e.preventDefault()
        e.stopPropagation()

        // Refocus the element after a small delay to ensure the blur has been processed
        setTimeout(() => {
          if (target && typeof target.focus === 'function') {
            target.focus()
          }
        }, 0)
        return false
      }

      this.setState({ fieldFocused: false })
      if (onBlurFn) {
        onBlurFn(e)
      }
    }

    const newDisabled = disabled || pendingApproval

    const labelProps: Record<string, unknown> = {
      compact,
      hideLabel,
      showAnchor,
      showError,
      showErrorAbove,
      helpContent,
      error,
      warning,
      className,
      elmId,
      inputName:
        typeof input === 'object' && input !== null
          ? (input as FieldInput).name
          : undefined,
      required,
      fieldFocused,
      pendingApproval,
      tooltipIconStyle,
    }

    // only include sublabel if it exists
    if (restProps.subLabel) {
      labelProps.subLabel = restProps.subLabel
    }

    const checkboxLabelProps = {
      label,
      checkboxLabel,
      showAnchor,
      helpContent,
      pendingApproval,
    }

    return (
      <Form.Field
        error={showError}
        className={cx('field', { compact }, { relaxed })}
      >
        {/*
         * This standard label is for non-checkbox components (check box and radio buttons)
         * We can't reuse this without refactoring it such that the top element is a label, so instead we
         * pass in the checkboxLabel below in those cases.
         */}
        {!isCheckbox &&
          !hideWrapper &&
          (!hideLabel || showAnchor) &&
          StandardFieldLabel.createFromLabel(label, labelProps)}
        {render(
          {
            ...restProps,
            disabled: newDisabled,
            onFocus: onFocusNew,
            onBlur: onBlurNew,
            compact,
            className,
            elmId,
            required,
            ...(input ? { input, focus: this.state.fieldFocused } : {}),
            // props that we only send to checkboxes
            ...(isCheckbox ? checkboxLabelProps : {}),
          },
          showError
        )}
        {!hideWrapper && showErrorBelow && (
          <FieldError
            error={error}
            showError={showError}
            className={className}
            elmId={elmId}
            disabled={newDisabled}
            {...restProps}
          />
        )}
        {Footer && <Footer />}
      </Form.Field>
    )
  }
}

/**
 * Extracts the "view" version of the field based on how the data is held in value
 *
 * TODO - remove this logic and pass in a renderViewMode or something to let
 * the wrapped element control it's own view mode rendering.
 *
 * TODO - enhance logic for radio group fields to which currently only reflect the underlying value.  (Cross this bridge
 * when we get to it...)
 */
const getViewModeValue = (fieldProps: FieldProps): string => {
  const { value, inputLabel, type, checked } = fieldProps
  const isCheckbox = _.has(fieldProps, 'checked')

  let result = ''
  if (fieldProps.options) {
    if (_.isArray(value)) {
      // dropdown
      const options = _.map(value, val => {
        const option = _.find(fieldProps.options, { value: val })
        return option ? option.text : ''
      })
      result = options ? options.join(', ') : ''
    } else {
      const option = _.find(fieldProps.options, { value })
      result = option ? (option.text as string) : ''
    }
  } else if (
    _.isObject(value) &&
    (value as Record<string, unknown>).selectedAddressText
  ) {
    // address suggestion field
    result = (value as Record<string, unknown>).selectedAddressText as string
  } else if (type === 'password') {
    result = '●'.repeat((value as string | undefined)?.length ?? 0)
  } else if (isCheckbox) {
    result = checked ? 'Yes' : 'No'
  } else {
    const finalValue = _.isBoolean(value)
      ? String(value)
      : (value as string | undefined)

    // input, textarea, etc.
    result = result.replace(/\r/g, '<br>')
    result = finalValue ? `${inputLabel || ''}${finalValue || ''}` : ''
  }

  return result
}

const renderValue = (
  value: string,
  componentName?: string
): React.ReactNode => {
  if (componentName === 'RichTextField') {
    return <span dangerouslySetInnerHTML={{ __html: value }} />
  }

  if (typeof value === 'string' && value.indexOf('\n') !== -1) {
    return value.split('\n').map((item, key) => {
      return (
        <span key={key}>
          {item}
          <br />
        </span>
      )
    })
  }

  return value || <span>{'\u00a0'}</span>
}

// lengths just used internally here - to send to underlying Placeholder component
const LENGTHS = {
  FULL: 'full',
  VERY_LONG: 'very long',
  LONG: 'long',
  MEDIUM: 'medium',
  SHORT: 'short',
  VERY_SHORT: 'very short',
} as const

/**
 * In determining desired length, always throw in some randomness relative to the desired length,
 * with the exception of FULL which is always FULL.
 *
 * NOTE: only randomize after mount - to avoid a server/client rendering mismatch
 * @param lengthRequested
 */
const getPlaceholderLength = (
  lengthRequested: FieldPlaceholderLength = FIELD_PLACEHOLDER_LENGTHS.MEDIUM,
  isMounted: boolean
): (typeof LENGTHS)[keyof typeof LENGTHS] | undefined => {
  if (!isMounted) {
    return lengthRequested
  }

  const getRand = (): number => Math.floor(Math.random() * 3)
  let resultLength: (typeof LENGTHS)[keyof typeof LENGTHS] | undefined
  switch (lengthRequested) {
    case FIELD_PLACEHOLDER_LENGTHS.FULL:
      resultLength = LENGTHS.FULL
      break
    case FIELD_PLACEHOLDER_LENGTHS.SHORT:
      resultLength = [LENGTHS.VERY_SHORT, LENGTHS.SHORT, LENGTHS.MEDIUM][
        getRand()
      ]
      break
    case FIELD_PLACEHOLDER_LENGTHS.LONG:
      resultLength = [LENGTHS.MEDIUM, LENGTHS.LONG, LENGTHS.VERY_LONG][
        getRand()
      ]
      break
    default:
      resultLength = [LENGTHS.SHORT, LENGTHS.MEDIUM, LENGTHS.LONG][getRand()]
      break
  }
  return resultLength
}

interface RenderViewModeProps extends FieldProps {
  viewModeRenderer?: (value: unknown) => React.ReactNode
}

const RenderViewMode: React.FC<RenderViewModeProps> = ({
  viewModeRenderer,
  ...fieldProps
}) => {
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  const {
    elmId,
    label,
    helpContent,
    breakAll,
    hideWrapper,
    hideLabel,
    labelOnTop = true,
    borderBottom,
    normalFont = false,
    showTooltip,
    compact,
    isLoading,
    loadingPlaceholderLength,
    componentName,
    tooltipIconStyle,
  } = fieldProps
  const value = getViewModeValue(fieldProps)
  const showAnchor = showTooltip // only show tooltip if specifically asked for (in view mode)
  const content = viewModeRenderer
    ? viewModeRenderer(fieldProps.value)
    : renderValue(value, componentName)

  return (
    <div
      className={cx(
        'view',
        { compact },
        { 'horizontal-layout': !labelOnTop },
        { 'normal-font': normalFont },
        { 'border-default': !borderBottom },
        { 'border-bottom-always': borderBottom === 'always' },
        { 'border-bottom-none': borderBottom === 'none' },
        { 'border-bottom-small-screens': borderBottom === 'smallScreensOnly' }
      )}
    >
      {!hideWrapper && !hideLabel && (
        <div data-elm-id={`${elmId}_label`} className={cx('label')}>
          {label}
        </div>
      )}
      <div
        data-elm-id={`${elmId}_value`}
        className={cx('value', { 'break-all': breakAll })}
      >
        {isLoading ? (
          <Placeholder className={cx('loading-placeholder')}>
            <Placeholder.Line
              length={getPlaceholderLength(loadingPlaceholderLength, isMounted)}
            />
          </Placeholder>
        ) : (
          content
        )}
      </div>
      {!hideWrapper && showAnchor && (
        <div className={cx('tooltip')}>
          <FieldTooltip
            helpContent={helpContent as string | undefined}
            iconStyle={tooltipIconStyle as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            showAnchor={showAnchor}
            label={typeof label === 'string' ? label : undefined}
            error={fieldProps.error}
            elmId={elmId}
          />
        </div>
      )}
    </div>
  )
}

/**
 * formFieldWrapper essentially adorns a field with things like label, info and error UI...
 *
 * This is a wrapper function that takes as its input, a render function that is
 * responsible for rendering the inner input control.  That render function should accept a
 * param called 'fieldProps' that will be passed down from the caller.
 *
 * Practically speaking the wrapped function created by THIS function will be in turn wrapped
 * by redux-form's Field (passed as a 'component' to it). Redux-form's Field will deal with
 * passing down the field params accordingly.
 *
 * The props this HOC handles are:
 *  viewMode, viewModeRenderer, touched, error, helpContent, breakAll, inputLabel
 */
export const formFieldWrapper =
  (render: RenderFn) =>
  (
    fieldProps: FieldProps & {
      viewMode?: boolean
      viewModeRenderer?: (value: unknown) => React.ReactNode
    }
  ): React.ReactElement => {
    const { viewMode, viewModeRenderer, ...restProps } = fieldProps
    const theme = useApplicationTheme()
    const newCompact = getThemedCompact(theme, restProps.compact)

    return viewMode ? (
      <RenderViewMode
        viewModeRenderer={viewModeRenderer}
        {...restProps}
        compact={newCompact}
      />
    ) : (
      <RenderEditMode
        render={render}
        fieldProps={{ ...restProps, compact: newCompact }}
      />
    )
  }

export { commonPropTypes, getViewModeValue, FIELD_PLACEHOLDER_LENGTHS }
